"use client";

import { Flame, Droplets, Gauge, Sprout, Waves, ArrowDownToLine, Wrench } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function ServiceCategories() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const services = [{
    id: 2,
    title: "Fire Pumps & Heat Exchanges",
    icon: Flame,
    image: "https://aaeagleplumbing.com/wp-content/uploads/2026/08/Fire-Pump-Work2.jpeg",
    description: "Fire pump systems and heat exchanger solutions",
    href: "https://aaeagleplumbing.com/services/fire-pumps-heat-exchangers/"
  }, {
    id: 3,
    title: "Chill Water Pumps & Cooling Towers",
    icon: Droplets,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1521681/img/chill_water_pump_cooling_tower.png",
    description: "Chill water pump and cooling tower maintenance",
    href: "https://aaeagleplumbing.com/services/chill-water-pumps-cooling-tower-service/"
  }, {
    id: 5,
    title: "Boiler Feed Pumps",
    icon: Gauge,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1521681/img/boiler_feed_pump_service.png",
    description: "Boiler feed pump installation and repair",
    href: "https://aaeagleplumbing.com/services/boiler-feed-pump-service/"
  }, {
    id: 6,
    title: "Irrigation & Pool Services",
    icon: Sprout,
    image: "https://aaeagleplumbing.com/wp-content/uploads/2026/08/Irrigation_system_work.jpg",
    description: "Comprehensive irrigation and pool system services",
    href: "https://aaeagleplumbing.com/services/irrigation-pool-services/"
  }, {
    id: 10,
    title: "Booster Pumps",
    icon: Waves,
    image: "https://aaeagleplumbing.com/wp-content/uploads/2026/08/domestic-booster-system.png",
    description: "Booster pump installation and maintenance",
    href: "https://aaeagleplumbing.com/services/booster-pump-service/"
  }, {
    id: 11,
    title: "Sewage Lift Stations",
    icon: ArrowDownToLine,
    image: "https://aaeagleplumbing.com/wp-content/uploads/2026/08/Liftstation_pump.jpg",
    description: "Sewage lift station servicing and repair",
    href: "https://aaeagleplumbing.com/services/sewage-lift-station/"
  }, {
    id: 13,
    title: "Line Shaft Turbines",
    icon: Wrench,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1521681/img/line_shaft_turbine_service.png",
    description: "Line shaft turbine maintenance and repair",
    href: "https://aaeagleplumbing.com/services/line-shaft-turbines/"
  }, {
    id: 14,
    title: "Submersible Pumps",
    icon: Droplets,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1521681/img/submersible_pump_service.png",
    description: "Submersible pump installation and service",
    href: "https://aaeagleplumbing.com/services/submersible-pumps/"
  }];
  return <section ref={ref} className={`relative py-20 bg-background transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
      <div className="container mx-auto px-6">
        
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            Water & Fire Pump Services
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Expert water pump solutions for commercial and residential applications
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => {
          const Icon = service.icon;
          const isExternal = service.href.startsWith("http");
          return <Link key={service.id} to={service.href} newTab={isExternal} className="block outline-none focus-visible:ring-2 focus-visible:ring-primary">
                <Card data-index={i} className={`group cursor-pointer h-full transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 rounded-[12px] bg-light-background/60 backdrop-blur-md border border-muted shadow-[0_2px_8px_rgb(0_0_0_/_0.04),_0_1px_3px_rgb(0_0_0_/_0.08)] hover:shadow-[0_4px_16px_rgb(0_0_0_/_0.12),_0_2px_6px_rgb(0_0_0_/_0.08)] overflow-hidden ${isVisible ? "animate-fade-in-up" : "opacity-0"}`} style={{
              animationDelay: `${i * 50}ms`
            }}>
                  
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-background/60 to-transparent"></div>
                  </div>

                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center transition-colors duration-[250ms] group-hover:bg-primary-300">
                        <Icon className="w-6 h-6 text-primary transition-colors duration-[250ms]" strokeWidth={1.5} />
                      </div>
                      <CardTitle className="text-lg font-display font-semibold text-foreground group-hover:text-primary transition-colors duration-200">
                        {service.title}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </Link>;
        })}
        </div>
      </div>

      {/* Decorative bottom line */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-secondary"></div>
    </section>;
}